import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pencil, Plus } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { MediaField } from "@/components/admin/MediaField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ADDON_AVAILABILITY,
  listAddonsAdmin,
  reorderAddons,
  setAddonFlags,
  upsertAddon,
  upsertAddonCategory,
  type AddonAdminRow,
  type AddonCategoryRow,
} from "@/lib/admin/addons.functions";

export const Route = createFileRoute("/_admin/admin/addons")({
  head: () => ({ meta: [{ title: "Add-ons · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminAddonsPage,
});

const slugify = (v: string) =>
  v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

function websiteReason(a: AddonAdminRow, cats: AddonCategoryRow[]): string | null {
  const cat = cats.find((c) => c.id === a.category_id);
  if (!a.is_active) return "Switched off";
  if (!a.is_public) return "Hidden from website";
  if (cat && !cat.is_active) return `Category "${cat.name}" is hidden`;
  if (a.availability_status === "out_of_stock") return "Out of stock";
  if (a.availability_status === "seasonal_unavailable") return "Seasonally unavailable";
  return null;
}

function errText(e: unknown) {
  const m = (e as Error)?.message ?? "";
  if (m.includes("duplicate_slug")) return "That slug is already used. Choose another.";
  return "Could not save. Please check the fields and try again.";
}

function AdminAddonsPage() {
  const listFn = useServerFn(listAddonsAdmin);
  const flagsFn = useServerFn(setAddonFlags);
  const reorderFn = useServerFn(reorderAddons);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "addons"], queryFn: () => listFn() });
  const items = q.data?.items ?? [];
  const cats = q.data?.categories ?? [];
  const [edit, setEdit] = useState<AddonAdminRow | "new" | null>(null);
  const [editCat, setEditCat] = useState<AddonCategoryRow | "new" | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "addons"] });

  const flags = useMutation({
    mutationFn: (v: { id: string; is_active?: boolean; is_public?: boolean; availability_status?: AddonAdminRow["availability_status"] }) => flagsFn({ data: v }),
    onSuccess: () => { toast.success("Saved"); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const move = async (idx: number, dir: -1 | 1) => {
    const ids = items.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
    try { await reorderFn({ data: { ids } }); refresh(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-8">
      <AdminPageHeader
        title="Add-ons"
        description="Extras sold on the À la carte page only. They never appear in meal plans."
        actions={<Button onClick={() => setEdit("new")}><Plus className="mr-1 h-4 w-4" /> New add-on</Button>}
      />
      {q.error ? <Alert><AlertDescription>Could not load add-ons.</AlertDescription></Alert> : null}
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2">Order</th><th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Category</th><th className="px-3 py-2">Price</th>
              <th className="px-3 py-2">Stock</th><th className="px-3 py-2">Active</th>
              <th className="px-3 py-2">On website</th><th className="px-3 py-2">Website status</th><th />
            </tr>
          </thead>
          <tbody>
            {q.isLoading ? (
              <tr><td colSpan={9} className="px-3 py-6 text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-muted-foreground">No add-ons yet. Create your first add-on to show it on the À la carte page.</td></tr>
            ) : items.map((a, idx) => {
              const reason = websiteReason(a, cats);
              return (
                <tr key={a.id} className="border-t">
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" aria-label="Move up" disabled={idx === 0} onClick={() => move(idx, -1)}><ArrowUp className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" aria-label="Move down" disabled={idx === items.length - 1} onClick={() => move(idx, 1)}><ArrowDown className="h-4 w-4" /></Button>
                    </div>
                  </td>
                  <td className="px-3 py-2"><div className="font-medium">{a.name}</div><div className="text-xs text-muted-foreground">{a.slug}</div></td>
                  <td className="px-3 py-2">{cats.find((c) => c.id === a.category_id)?.name ?? "—"}</td>
                  <td className="px-3 py-2 tabular-nums">₹{(a.price_paise / 100).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <select aria-label="Stock status" className="rounded-md border bg-background px-2 py-1"
                      value={a.availability_status}
                      onChange={(e) => flags.mutate({ id: a.id, availability_status: e.target.value as AddonAdminRow["availability_status"] })}>
                      {ADDON_AVAILABILITY.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2"><Switch aria-label="Active" checked={a.is_active} onCheckedChange={(v) => flags.mutate({ id: a.id, is_active: v })} /></td>
                  <td className="px-3 py-2"><Switch aria-label="Show on website" checked={a.is_public} onCheckedChange={(v) => flags.mutate({ id: a.id, is_public: v })} /></td>
                  <td className="px-3 py-2">
                    {reason ? (<div className="space-y-1"><Badge variant="outline">Not on website</Badge><div className="text-xs text-destructive">{reason}</div></div>) : <Badge>Live</Badge>}
                  </td>
                  <td className="px-3 py-2"><Button size="icon" variant="ghost" aria-label="Edit" onClick={() => setEdit(a)}><Pencil className="h-4 w-4" /></Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Live add-ons appear only for delivery areas and dates the kitchen delivers to. Add-ons that were ever ordered can be switched off but not deleted.</p>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Categories</h2>
          <Button variant="outline" onClick={() => setEditCat("new")}><Plus className="mr-1 h-4 w-4" /> New category</Button>
        </div>
        {cats.length === 0 ? <p className="text-sm text-muted-foreground">No categories. Add-ons without a category show under "Other".</p> : (
          <ul className="divide-y rounded-xl border">
            {cats.map((c) => (
              <li key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{c.name} <span className="text-xs text-muted-foreground">({c.slug})</span></span>
                <span className="flex items-center gap-2">
                  {c.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Hidden</Badge>}
                  <Button size="icon" variant="ghost" aria-label="Edit category" onClick={() => setEditCat(c)}><Pencil className="h-4 w-4" /></Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {edit ? <AddonDialog row={edit === "new" ? null : edit} cats={cats} onClose={() => setEdit(null)} onSaved={refresh} /> : null}
      {editCat ? <CategoryDialog row={editCat === "new" ? null : editCat} onClose={() => setEditCat(null)} onSaved={refresh} /> : null}
    </div>
  );
}

function AddonDialog({ row, cats, onClose, onSaved }: { row: AddonAdminRow | null; cats: AddonCategoryRow[]; onClose: () => void; onSaved: () => void }) {
  const saveFn = useServerFn(upsertAddon);
  const [f, setF] = useState({
    name: row?.name ?? "", slug: row?.slug ?? "", description: row?.description ?? "",
    image_url: row?.image_url ?? "", alt_text: row?.alt_text ?? "", category_id: row?.category_id ?? "",
    price: row ? (row.price_paise / 100).toString() : "",
    availability_status: row?.availability_status ?? "in_stock",
    is_active: row?.is_active ?? true, is_public: row?.is_public ?? true,
  });
  const [slugTouched, setSlugTouched] = useState(!!row);
  const [error, setError] = useState<string | null>(null);
  const priceNum = Number(f.price);
  const save = useMutation({
    mutationFn: () => {
      if (!f.name.trim()) throw new Error("Name is required.");
      if (!f.slug.trim()) throw new Error("Slug is required.");
      if (f.price.trim() === "" || !Number.isFinite(priceNum) || priceNum < 0) throw new Error("Enter a price of zero or more.");
      return saveFn({ data: { id: row?.id, input: {
        name: f.name, slug: f.slug, description: f.description, image_url: f.image_url, alt_text: f.alt_text,
        category_id: f.category_id || null, price_paise: Math.round(priceNum * 100),
        availability_status: f.availability_status, is_active: f.is_active, is_public: f.is_public,
      } } });
    },
    onSuccess: () => { toast.success("Add-on saved"); onSaved(); onClose(); },
    onError: (e) => setError((e as Error).message.match(/required|price/i) ? (e as Error).message : errText(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{row ? "Edit add-on" : "New add-on"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label htmlFor="ad-name">Name *</Label>
            <Input id="ad-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: slugTouched ? f.slug : slugify(e.target.value) })} /></div>
          <div><Label htmlFor="ad-slug">Slug (unique ID) *</Label>
            <Input id="ad-slug" value={f.slug} onChange={(e) => { setSlugTouched(true); setF({ ...f, slug: slugify(e.target.value) }); }} /></div>
          <div><Label htmlFor="ad-desc">Description</Label>
            <Textarea id="ad-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div><Label>Image</Label>
            <MediaField value={f.image_url} kind="image" onChange={(v) => setF({ ...f, image_url: v })} onAltSuggestion={(alt) => setF((s) => ({ ...s, alt_text: s.alt_text || alt }))} /></div>
          <div><Label htmlFor="ad-alt">Image description (alt text)</Label>
            <Input id="ad-alt" value={f.alt_text} onChange={(e) => setF({ ...f, alt_text: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="ad-price">Price (₹) *</Label>
              <Input id="ad-price" type="number" min={0} step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
            <div><Label htmlFor="ad-cat">Category</Label>
              <select id="ad-cat" className="h-10 w-full rounded-md border bg-background px-2" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}>
                <option value="">None</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
          </div>
          <div><Label htmlFor="ad-stock">Stock status</Label>
            <select id="ad-stock" className="h-10 w-full rounded-md border bg-background px-2" value={f.availability_status}
              onChange={(e) => setF({ ...f, availability_status: e.target.value as typeof f.availability_status })}>
              {ADDON_AVAILABILITY.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select></div>
          <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} /></div>
          <div className="flex items-center justify-between"><Label>Show on website</Label><Switch checked={f.is_public} onCheckedChange={(v) => setF({ ...f, is_public: v })} /></div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending} onClick={() => { setError(null); save.mutate(); }}>{save.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ row, onClose, onSaved }: { row: AddonCategoryRow | null; onClose: () => void; onSaved: () => void }) {
  const saveFn = useServerFn(upsertAddonCategory);
  const [f, setF] = useState({ name: row?.name ?? "", slug: row?.slug ?? "", sort_order: row?.sort_order ?? 0, is_active: row?.is_active ?? true });
  const save = useMutation({
    mutationFn: () => saveFn({ data: { id: row?.id, input: { ...f, description: row?.description ?? null } } }),
    onSuccess: () => { toast.success("Category saved"); onSaved(); onClose(); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{row ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label htmlFor="ac-name">Name *</Label><Input id="ac-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: row ? f.slug : slugify(e.target.value) })} /></div>
          <div><Label htmlFor="ac-slug">Slug *</Label><Input id="ac-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} /></div>
          <div><Label htmlFor="ac-sort">Display order</Label><Input id="ac-sort" type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} /></div>
          <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending || !f.name.trim() || !f.slug} onClick={() => save.mutate()}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
